import React, { useRef, useState, useEffect } from "react";
import { BookOpen, Volume2, X, Feather, ShoppingCart } from "lucide-react";
import scoc from "../../src/images/scoc.png";
import chapter1Image from "../../src/images/chapter1.webp";
import book2 from "../../src/images/book2.png";
import book3 from "../../src/images/book3.png";
import book4 from "../../src/images/book4.png";
import book5 from "../../src/images/book5.jpg";
import book6 from "../../src/images/book6.png";
import book7 from "../../src/images/book7.png";
import book8 from "../../src/images/book8.jpg";
import Header from "../components/Header";
import Footer from "../components/Footer";

const DemoPreview = () => {
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedBook, setSelectedBook] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const sections = {
    intro: useRef(null),
    chapter1: useRef(null),
    chapter2: useRef(null),
    audio: useRef(null),
  };

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const bookSeries = [
    {
      id: 1,
      title: "The Sinister Coven of Cruor",
      cover: scoc,
      description: "Begin the epic journey with the first installment of the series. Follow our hero as they discover their true destiny.",
      purchaseLink: "#"
    },
    {
      id: 2,
      title: "The Children of the Night",
      cover: book2,
      description: "The adventure continues as dark forces gather and alliances are tested in this thrilling sequel.",
      purchaseLink: "#"
    },
    {
      id: 3,
      title: "Awakening the Grey",
      cover: book3,
      description: "Secrets are revealed and loyalties are questioned in this pivotal third installment.",
      purchaseLink: "#"
    },
    {
      id: 4,
      title: "The Saturnian Talisman",
      cover: book4,
      description: "Everything comes to a head in this explosive fourth book as our heroes face their greatest challenge yet.",
      purchaseLink: "#"
    },
    {
      id: 5,
      title: "Into the Stygia",
      cover: book5,
      description: "The stunning conclusion to the epic saga brings new hope and unexpected twists.",
      purchaseLink: "#"
    },
    {
      id: 6,
      title: "LWC Origins: The Elder Witches",
      cover: book6,
      description: "Explore the origins of the ancient order in this captivating prequel.",
      purchaseLink: "#"
    },
    {
      id: 7,
      title: "LWC Origins: The Nocturnal Baron",
      cover: book7,
      description: "Discover the dark history behind the mysterious Baron in this origin story.",
      purchaseLink: "#"
    },
    {
      id: 8,
      title: "LWC Origins: The Pharaoh's Pact",
      cover: book8,
      description: "An ancient covenant awakens in this thrilling addition to the LWC universe.",
      purchaseLink: "#"
    }
  ];

  const scrollToSection = (section) => {
    sections[section].current.scrollIntoView({ behavior: "smooth" });
  };

  const togglePopup = () => {
    setIsPopupOpen(!isPopupOpen);
  };

  const openBookOverlay = (book) => {
    setSelectedBook(book);
  };

  const closeBookOverlay = () => {
    setSelectedBook(null);
  };

  return (
    <div className="min-h-screen bg-cream-50">
      <Header />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0 opacity-[0.02]" style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%231a1a2e' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`
        }} />

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className={`text-center max-w-4xl mx-auto transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold-500" />
              <span className="text-gold-600 text-sm font-sans tracking-[0.2em] uppercase">
                Interactive Demo
              </span>
              <span className="w-8 h-px bg-gold-500" />
            </div>

            <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-ink-900 mb-6">
              HTML Book Preview
              <span className="block mt-2 text-gradient-gold">Experience</span>
            </h1>

            <p className="font-body text-xl text-ink-600 leading-relaxed max-w-2xl mx-auto">
              Give your readers an immersive experience. Share preview chapters,
              grant exclusive access for members, or offer audio read-alongs.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 lg:py-20">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="grid lg:grid-cols-3 gap-8 lg:gap-12">

            {/* Left - Table of Contents */}
            <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
              <div className="lg:sticky lg:top-28 bg-ink-900 p-8 lg:p-10 border border-ink-800">
                {/* Gold Corner */}
                <div className="absolute top-0 right-0 w-16 h-16 overflow-hidden">
                  <div className="absolute top-0 right-0 w-24 h-24 bg-gradient-to-bl from-gold-500/20 to-transparent transform rotate-45 translate-x-12 -translate-y-12" />
                </div>

                <div className="flex items-center gap-3 mb-8">
                  <BookOpen className="w-6 h-6 text-gold-400" />
                  <h2 className="font-display text-xl font-semibold text-cream-50">Table of Contents</h2>
                </div>

                <nav className="space-y-3">
                  {[
                    { id: 'intro', label: 'Introduction' },
                    { id: 'chapter1', label: 'Chapter 1: The Beginning' },
                    { id: 'chapter2', label: 'Chapter 2: The Conflict' },
                    { id: 'audio', label: 'Audio Version' },
                  ].map((item) => (
                    <button
                      key={item.id}
                      onClick={() => scrollToSection(item.id)}
                      className="w-full text-left px-4 py-3 text-cream-300 hover:text-cream-50 hover:bg-ink-800 transition-all duration-300 font-body flex items-center gap-3"
                    >
                      <span className="w-2 h-px bg-gold-500" />
                      {item.label}
                    </button>
                  ))}
                </nav>

                <div className="mt-8 pt-8 border-t border-ink-700">
                  <button
                    onClick={togglePopup}
                    className="w-full btn-gold text-sm"
                  >
                    <span className="flex items-center justify-center gap-2">
                      <Feather className="w-4 h-4" />
                      See Book Popup
                    </span>
                  </button>
                </div>
              </div>
            </div>

            {/* Right - Preview Content */}
            <div className={`lg:col-span-2 transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
              <div className="bg-cream-100 border border-cream-200 shadow-luxury">
                <div className="p-8 lg:p-12">

                  {/* Introduction */}
                  <div ref={sections.intro} className="mb-12 pb-12 border-b border-cream-300">
                    <h2 className="font-display text-3xl font-bold text-ink-900 mb-6">Introduction</h2>
                    <p className="font-body text-ink-700 leading-relaxed text-lg">
                      Welcome to the HTML Book Preview Demo! Here, you can see how
                      beautifully formatted book previews can engage your readers.
                      Whether it's a gripping prologue or an enticing first chapter,
                      HTML previews offer a web-friendly way to showcase your work.
                    </p>
                  </div>

                  {/* Chapter 1 */}
                  <div ref={sections.chapter1} className="mb-12 pb-12 border-b border-cream-300">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="px-3 py-1 bg-gold-100 text-gold-700 font-sans text-sm tracking-wide">
                        Chapter One
                      </span>
                    </div>
                    <h2 className="font-display text-3xl font-bold text-ink-900 mb-2">The Beginning</h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-gold-400 to-gold-600 mb-6" />

                    <p className="font-body text-ink-700 leading-relaxed text-lg mb-8">
                      Nestled in a quiet clearing, surrounded by the ancient and towering trees of
                      the mountain forest, stood a small cabin. Its aged wooden exterior blended
                      seamlessly with the natural surroundings, as if nature itself had constructed the
                      dwelling. A birdsong filled the air, harmonizing with the gentle rustling of leaves
                      in the breeze, creating a symphony of tranquility. The seclusion of this haven
                      made it the ideal sanctuary for the Leclair sisters to practice their magical arts
                      without fear of discovery or interruption.
                    </p>

                    <div className="relative rounded-sm overflow-hidden shadow-luxury mb-6">
                      <img
                        src={chapter1Image}
                        alt="Chapter 1 illustration"
                        className="w-full"
                      />
                      <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-ink-900/80 to-transparent p-6">
                        <p className="text-cream-100 font-accent italic">
                          The cabin in the clearing — where magic awakens
                        </p>
                      </div>
                    </div>

                    <p className="font-body text-ink-500 italic">
                      Get the book to continue reading...
                    </p>
                  </div>

                  {/* Chapter 2 */}
                  <div ref={sections.chapter2} className="mb-12 pb-12 border-b border-cream-300">
                    <div className="flex items-center gap-3 mb-6">
                      <span className="px-3 py-1 bg-gold-100 text-gold-700 font-sans text-sm tracking-wide">
                        Chapter Two
                      </span>
                    </div>
                    <h2 className="font-display text-3xl font-bold text-ink-900 mb-2">The Conflict</h2>
                    <div className="w-16 h-1 bg-gradient-to-r from-gold-400 to-gold-600 mb-6" />

                    <p className="font-body text-ink-700 leading-relaxed text-lg mb-6">
                      The secluded mountain cabin was nestled in the heart of the woods, surrounded by towering trees and a thick blanket of fog. It was here that
                      Cassandra Leclair stood, her long dark hair cascading down her back as she raised
                      her hands to the sky. Her piercing eyes focused on the swirling energy before her,
                      a manifestation of her raw magical power.
                    </p>

                    <blockquote className="border-l-4 border-gold-400 pl-6 py-2 my-8 bg-cream-50">
                      <p className="font-accent text-xl text-ink-700 italic">
                        "Remember, Cassandra," Selene cautioned, her wavy chestnut hair framing her
                        gentle face. "Control is key."
                      </p>
                    </blockquote>

                    <p className="font-body text-ink-700 leading-relaxed text-lg mb-6">
                      Cassandra nodded, exhaling deeply as she guided the energy into the form of
                      a roaring lion. She could feel its strength, its fierce determination — a reflection
                      of her own ambition. The lion paced the wooden floor, leaving behind glowing
                      embers in its wake.
                    </p>

                    <p className="font-body text-ink-500 italic">
                      Get the book to continue reading...
                    </p>
                  </div>

                  {/* Audio Section */}
                  <div ref={sections.audio}>
                    <div className="flex items-center gap-3 mb-6">
                      <Volume2 className="w-6 h-6 text-gold-600" />
                      <h2 className="font-display text-3xl font-bold text-ink-900">Audio Version</h2>
                    </div>
                    <p className="font-body text-ink-700 leading-relaxed text-lg mb-6">
                      Prefer to listen? Enjoy the first chapter of the audio version below.
                    </p>

                    <div className="bg-ink-900 p-6 rounded-sm">
                      <audio
                        controls
                        className="w-full"
                        style={{
                          filter: 'invert(1)',
                        }}
                      >
                        <source
                          src="https://www.soundhelix.com/examples/mp3/SoundHelix-Song-1.mp3"
                          type="audio/mpeg"
                        />
                        Your browser does not support the audio element.
                      </audio>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Book Series Showcase Section */}
      <section className="py-24 lg:py-32 bg-ink-900 relative overflow-hidden">
        {/* Background */}
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gold-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[500px] h-[500px] bg-gold-400/5 rounded-full blur-3xl" />
        </div>

        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-3 mb-6">
              <span className="w-8 h-px bg-gold-500" />
              <span className="text-gold-400 text-sm font-sans tracking-[0.2em] uppercase">
                Book Series
              </span>
              <span className="w-8 h-px bg-gold-500" />
            </div>

            <h2 className="font-display text-4xl md:text-5xl font-bold text-cream-50 mb-6">
              Showcase Your
              <span className="block mt-2 text-gradient-gold">Entire Collection</span>
            </h2>

            <p className="font-body text-lg text-cream-300 max-w-2xl mx-auto leading-relaxed">
              Create stunning book series displays that captivate your readers.
              Each cover invites interaction with elegant animations.
            </p>
          </div>

          {/* Book Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
            {bookSeries.map((book, index) => (
              <div
                key={book.id}
                className="group cursor-pointer"
                onClick={() => openBookOverlay(book)}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className="relative">
                  {/* Shadow */}
                  <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-4/5 h-6 bg-ink-950/50 blur-xl rounded-full group-hover:bg-gold-500/20 transition-colors duration-500" />

                  {/* Book */}
                  <div className="relative transform transition-all duration-500 group-hover:-translate-y-3 group-hover:rotate-1">
                    {/* Spine Effect */}
                    <div className="absolute left-0 top-0 bottom-0 w-3 bg-gradient-to-r from-ink-950/40 to-transparent rounded-l-sm" />

                    <img
                      src={book.cover}
                      alt={book.title}
                      className="w-full aspect-[2/3] object-cover rounded-sm shadow-book group-hover:shadow-luxury-lg transition-shadow duration-500"
                    />

                    {/* Hover Overlay */}
                    <div className="absolute inset-0 bg-gradient-to-t from-ink-900/90 via-ink-900/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-sm flex items-end p-4">
                      <span className="text-cream-50 font-sans text-sm tracking-wide">
                        View Details →
                      </span>
                    </div>

                    {/* Gold Corner */}
                    <div className="absolute top-0 right-0 w-8 h-8 overflow-hidden opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="absolute top-0 right-0 w-12 h-12 bg-gradient-to-bl from-gold-400 to-transparent transform rotate-45 translate-x-6 -translate-y-6" />
                    </div>
                  </div>
                </div>

                {/* Title */}
                <h3 className="mt-4 font-display text-sm text-cream-200 text-center group-hover:text-gold-400 transition-colors">
                  {book.title}
                </h3>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Book Overlay Modal */}
      {selectedBook && (
        <div
          className="fixed inset-0 bg-ink-950/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={closeBookOverlay}
        >
          <div
            className="relative bg-cream-50 border border-cream-200 shadow-luxury-lg max-w-3xl w-full transform transition-all animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={closeBookOverlay}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-ink-500 hover:text-ink-900 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="p-8 lg:p-12">
              <div className="flex flex-col md:flex-row gap-8 lg:gap-12">
                {/* Book Cover */}
                <div className="flex-shrink-0">
                  <div className="relative">
                    <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-4/5 h-6 bg-ink-900/20 blur-xl rounded-full" />
                    <img
                      src={selectedBook.cover}
                      alt={selectedBook.title}
                      className="w-48 md:w-56 h-auto shadow-book rounded-sm"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="flex-1">
                  <span className="text-gold-600 text-sm font-sans tracking-wide">
                    Featured Book
                  </span>
                  <h3 className="font-display text-3xl font-bold text-ink-900 mt-2 mb-4">
                    {selectedBook.title}
                  </h3>
                  <p className="font-body text-ink-600 text-lg leading-relaxed mb-8">
                    {selectedBook.description}
                  </p>

                  <a
                    href={selectedBook.purchaseLink}
                    className="btn-gold inline-flex"
                  >
                    <span className="flex items-center gap-2">
                      <ShoppingCart className="w-5 h-5" />
                      Purchase Now
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Book Popup Preview */}
      {isPopupOpen && (
        <div
          className="fixed inset-0 bg-ink-950/90 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
          onClick={togglePopup}
        >
          <div
            className="relative bg-cream-50 border border-cream-200 shadow-luxury-lg max-w-md w-full animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={togglePopup}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center text-ink-500 hover:text-ink-900 transition-colors z-10"
            >
              <X className="w-6 h-6" />
            </button>

            <div className="p-8 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-4/5 h-6 bg-ink-900/20 blur-xl rounded-full" />
                <img
                  src={scoc}
                  alt="Book Cover"
                  className="w-40 h-auto shadow-book rounded-sm"
                />
              </div>

              <h3 className="font-display text-2xl font-bold text-ink-900 mb-3">
                The Amazing Book Title
              </h3>
              <p className="font-body text-ink-600 mb-8">
                This is a summary of the book. It highlights key aspects of the
                story and gives readers an exciting reason to buy.
              </p>

              <button className="btn-gold">
                <span className="flex items-center gap-2">
                  <ShoppingCart className="w-5 h-5" />
                  Buy Now
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DemoPreview;
